import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Select, Skeleton, Space, Statistic,
  Table, Tag, Tabs, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
  CreateRouteRequest, CreateVehicleRequest, RouteRow, VehicleKind, VehicleRow, VehicleStatus,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin surface — Transport.
 * Vehicles + routes; expiring-cert alerts drive the "fitness soon" list.
 */
const KIND_LABELS: Record<VehicleKind, string> = {
  bus: 'Bus', van: 'Van', auto: 'Auto', car: 'Car', other: 'Other',
};
const STATUS_TONE: Record<VehicleStatus, string> = {
  active: 'success', off_road: 'warning', retired: 'default',
};

export function TransportPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [vehOpen, setVehOpen] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);

  const t = useQuery({
    queryKey: ['transport', institution],
    queryFn: () => productApi.getTransport(institution),
    enabled: !!institution,
  });

  const vehicles = t.data?.vehicles ?? [];
  const routes = t.data?.routes ?? [];
  const expiring = t.data?.expiring_soon ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Transport</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Vehicles + routes with fitness &amp; insurance expiry tracking (30-day warning window).
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Vehicles" value={vehicles.length} /></Card>
        <Card size="small"><Statistic title="Active" value={vehicles.filter((v) => v.status === 'active').length} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Routes" value={routes.length} /></Card>
        <Card size="small"><Statistic title="Certs expiring soon" value={expiring.length} valueStyle={{ color: expiring.length > 0 ? '#a35800' : undefined }} /></Card>
      </div>

      {expiring.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Renewals due within 30 days"
          description={
            <Space wrap size={4}>
              {expiring.map((e, i) => (
                <Tag key={i} color="warning">{e.registration} · {e.what} · {e.date}</Tag>
              ))}
            </Space>
          }
        />
      )}

      <Tabs
        defaultActiveKey="vehicles"
        items={[
          {
            key: 'vehicles',
            label: `Vehicles (${vehicles.length})`,
            children: (
              <>
                <div style={{ marginBottom: 12, textAlign: 'right' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setVehOpen(true)}>Add vehicle</Button>
                </div>
                {t.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
                {vehicles.length === 0 ? (
                  <Empty description={<span>No vehicles. <a onClick={() => setVehOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
                ) : (
                  <Table<VehicleRow>
                    size="small"
                    pagination={{ pageSize: 15 }}
                    rowKey="id"
                    dataSource={vehicles}
                    columns={[
                      { title: 'Registration', dataIndex: 'registration', key: 'r', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                      { title: 'Kind', dataIndex: 'kind', key: 'k', width: 90, render: (v: VehicleKind) => <Tag>{KIND_LABELS[v]}</Tag> },
                      { title: 'Capacity', dataIndex: 'capacity', key: 'c', width: 90, align: 'right', render: (v: number) => v || '—' },
                      { title: 'Driver', dataIndex: 'driver_name', key: 'd', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
                      { title: 'Phone', dataIndex: 'driver_phone', key: 'dp', width: 140, render: (v: string | null) => v ? <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> : '—' },
                      { title: 'Fitness until', dataIndex: 'fitness_until', key: 'fu', width: 130, render: (v: string | null) => v ?? '—' },
                      { title: 'Insurance until', dataIndex: 'insurance_until', key: 'iu', width: 130, render: (v: string | null) => v ?? '—' },
                      { title: 'Status', dataIndex: 'status', key: 's', width: 110, render: (v: VehicleStatus) => <Tag color={STATUS_TONE[v]}>{v.replace(/_/g, ' ')}</Tag> },
                    ]}
                  />
                )}
              </>
            ),
          },
          {
            key: 'routes',
            label: `Routes (${routes.length})`,
            children: (
              <>
                <div style={{ marginBottom: 12, textAlign: 'right' }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setRouteOpen(true)} disabled={vehicles.length === 0}>Add route</Button>
                </div>
                {routes.length === 0 ? (
                  <Empty description="No routes yet." style={{ padding: 20 }} />
                ) : (
                  <Table<RouteRow>
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={routes}
                    columns={[
                      { title: 'Code', dataIndex: 'code', key: 'c', width: 100, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                      { title: 'Name', dataIndex: 'name', key: 'n', render: (v: string) => <b>{v}</b> },
                      { title: 'Vehicle', dataIndex: 'vehicle_registration', key: 'v', render: (v: string | null) => v ?? <Typography.Text type="secondary">unassigned</Typography.Text> },
                      { title: 'Stops', dataIndex: 'stops', key: 'st', render: (v: string[]) => v.length > 0 ? <Space wrap size={4}>{v.map((s, i) => <Tag key={i}>{s}</Tag>)}</Space> : <Typography.Text type="secondary">—</Typography.Text> },
                    ]}
                  />
                )}
              </>
            ),
          },
        ]}
      />

      <NewVehicleModal open={vehOpen} onClose={() => setVehOpen(false)} institution={institution}
        onCreated={() => {
          setVehOpen(false);
          void qc.invalidateQueries({ queryKey: ['transport', institution] });
          void message.success('Vehicle added');
        }}
      />
      <NewRouteModal open={routeOpen} onClose={() => setRouteOpen(false)} institution={institution} vehicles={vehicles}
        onCreated={() => {
          setRouteOpen(false);
          void qc.invalidateQueries({ queryKey: ['transport', institution] });
          void message.success('Route added');
        }}
      />
    </Space>
  );
}

function NewVehicleModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: () => void }) {
  const [form] = Form.useForm<CreateVehicleRequest>();
  const m = useMutation({
    mutationFn: (v: CreateVehicleRequest) => productApi.createVehicle(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add vehicle" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ kind: 'bus' }} onFinish={(v) => m.mutate({
        ...v,
        registration: v.registration.trim().toUpperCase(),
        driver_name: v.driver_name?.trim() || undefined,
        driver_phone: v.driver_phone?.trim() || undefined,
        fitness_until: v.fitness_until || undefined,
        insurance_until: v.insurance_until || undefined,
      })}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="registration" label="Registration" rules={[{ required: true }]}>
            <Input placeholder="MH 12 AB 1234" autoFocus />
          </Form.Item>
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select options={(Object.keys(KIND_LABELS) as VehicleKind[]).map((k) => ({ value: k, label: KIND_LABELS[k] }))} />
          </Form.Item>
          <Form.Item name="capacity" label="Capacity">
            <InputNumber style={{ width: '100%' }} min={0} max={200} />
          </Form.Item>
          <Form.Item name="driver_name" label="Driver name">
            <Input />
          </Form.Item>
          <Form.Item name="driver_phone" label="Driver phone" rules={[{ pattern: /^[+0-9 -]{6,20}$/, message: 'digits, spaces, plus or dash' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fitness_until" label="Fitness until" rules={[{ pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
            <Input placeholder="yyyy-mm-dd" />
          </Form.Item>
          <Form.Item name="insurance_until" label="Insurance until" rules={[{ pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'yyyy-mm-dd' }]}>
            <Input placeholder="yyyy-mm-dd" />
          </Form.Item>
        </div>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function NewRouteModal({
  open, onClose, institution, vehicles, onCreated,
}: { open: boolean; onClose: () => void; institution: string; vehicles: VehicleRow[]; onCreated: () => void }) {
  const [form] = Form.useForm<{ code: string; name: string; vehicle_id?: string; stops_csv?: string }>();
  const m = useMutation({
    mutationFn: (v: CreateRouteRequest) => productApi.createRoute(institution, v),
    onSuccess: () => { form.resetFields(); onCreated(); },
  });
  const err = errMsg(m.error);
  return (
    <Modal open={open} title="Add route" onCancel={() => { m.reset(); onClose(); }} okText="Add" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => m.mutate({
        code: v.code.trim(),
        name: v.name.trim(),
        vehicle_id: v.vehicle_id ?? null,
        stops: v.stops_csv?.split(',').map((s) => s.trim()).filter(Boolean) ?? [],
      })}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="4B" autoFocus />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Katraj → School → Kondhwa" />
          </Form.Item>
        </div>
        <Form.Item name="vehicle_id" label="Vehicle (optional)">
          <Select allowClear options={vehicles.map((v) => ({ value: v.id, label: v.registration }))} />
        </Form.Item>
        <Form.Item name="stops_csv" label="Stops (comma separated)">
          <Input placeholder="Katraj, Bibvewadi, School, Kondhwa" />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

function errMsg(err: unknown): string | null {
  if (!err) return null;
  const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
  return body?.message ?? (err instanceof Error ? err.message : null);
}
